import * as React from 'react'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/react-hooks'
import debounce from 'lodash.debounce'

import { useTheme } from '@thg-commerce/enterprise-pages/src/Basket/theme'
import {
  QuantitySelector,
  QuantitySelectorProps,
} from '@thg-commerce/gravity-elements/QuantitySelector/QuantitySelector'
import {
  useHorizonSessionSettings,
  useBasketId,
  useSiteConfig,
  i18n,
  MessageArea,
  useTransmit,
  EnterpriseContext,
  BasketContext,
  useEnterpriseContext,
} from '@thg-commerce/enterprise-core'
import { useBackendEventNormaliser } from '@thg-commerce/enterprise-metrics'
import { pushToEnhancedDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'
import {
  ProductImage,
  AriaMessageType,
  Button,
  PlatformMessage,
} from '@thg-commerce/gravity-elements'
import { BasketData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Basket/Basket'
import {
  RemoveItemData,
  RemoveItemVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Mutation/RemoveProductFromBasket'
import {
  UpdateItemQuantityData,
  UpdateItemQuantityVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Mutation/UpdateProductQuantityInBasket'
import { Feature } from '@thg-commerce/enterprise-network/src/generated/graphql'

import {
  Container,
  TotalPrice,
  RemoveItem,
  StyledClose,
  UnitPrice,
  Discount,
  IconContainer,
  Name,
  ProductImageLink,
  RemoveItemContainer,
  QuantitySelectorContainer,
  QuantityPlaceholder,
  StyledPlatformMessage,
  MaxOrderMessage,
  WishlistContainer,
} from './styles'
import { Message } from './Message'

export const UPDATE_PRODUCT_QUANTITY_MUTATION = gql`
  mutation UpdateProductQuantityInBasket(
    $basketId: ID
    $itemId: ID!
    $quantity: Int!
    $settings: SessionSettings!
  ) {
    updateProductQuantityInBasket(
      basketId: $basketId
      itemId: $itemId
      quantity: $quantity
      settings: $settings
    ) @client {
      id
    }
  }
`

export const REMOVE_PRODUCT_MUTATION = gql`
  mutation RemoveProductFromBasket(
    $basketId: ID
    $itemId: ID!
    $settings: SessionSettings!
  ) {
    removeProductFromBasket(
      basketId: $basketId
      itemId: $itemId
      settings: $settings
    ) @client {
      id
    }
  }
`

const renderAnnouncer = (ariaLive: AriaMessageType, message: string) => {
  return <MessageArea aria-live={ariaLive} message={message} />
}

export type ItemProps = BasketData['items'][0] & {
  discountMessages?: string[]
}

export const Item = (props: ItemProps) => {
  const [basketId, setBasketId] = useBasketId()
  const sessionSettings = useHorizonSessionSettings()
  const {
    basketHideInitialPriceColumn,
    enableWishlists,
    enableWishlistsGlobal,
    enableWishlistOnBasketItem,
    enhancedEcommerceEnabled,
    showBasketItemOutOfStockMessage,
    useExternalIdentifier,
  } = useSiteConfig()

  const theme = useTheme()
  const transmit = useTransmit()
  const { normaliseBackendEvent, normaliserReady } = useBackendEventNormaliser(
    true,
  )
  const { extensionsRef } = React.useContext(EnterpriseContext)
  const {
    value: [getExtensions],
  } = extensionsRef
  const { horizonFeatures } = useEnterpriseContext()
  const removePressed = React.useRef(false)
  const quantityIsUpdating = React.useRef(false)

  const { presentClickAndCollectModal } = React.useContext(BasketContext)

  const showWishlist =
    enableWishlists && enableWishlistsGlobal && enableWishlistOnBasketItem

  const clickAndCollectEnabled =
    horizonFeatures?.includes(Feature.ClickAndCollect) || false

  const maxProductLimit = props.product.maxPerOrder || 99
  const maxProductQuantityReached = props.quantity >= maxProductLimit

  const hasDiscounts =
    props.discountMessages && props.discountMessages.length > 0

  const showItemErrors = props.messages && props.fulfilmentMethod ? true : false

  const trackEnhancedEvent = (event: string, key: string, quantity: number) => {
    if (enhancedEcommerceEnabled) {
      pushToEnhancedDataLayer({
        event,
        ecommerce: {
          [key]: {
            products: [
              {
                quantity,
                id:
                  useExternalIdentifier && props.product.externalIdentifier
                    ? props.product.externalIdentifier
                    : props.product.sku,
                price: props.standardPricePerUnit.amount,
                name: props.product.title,
                brand: props.product.product?.brand?.name,
              },
            ],
          },
        },
      })
    }
  }

  const trackBackendEvent = (quantity: number, subtype: string) => {
    const extensions = getExtensions()

    normaliserReady &&
      transmit({
        type: 'cart_interaction_event',
        payload: normaliseBackendEvent({
          eventData: {
            subtype,
            type: 'cart_interaction_event',
            items: [
              {
                quantity,
                product_group: {
                  id: props.product.product?.sku,
                  selected_variant: {
                    price: {
                      currency: sessionSettings.currency,
                      value: Number(props.standardPricePerUnit.amount),
                    },
                    sku: props.product.sku,
                  },
                  total_variants: props.product.product?.variants
                    ? props.product.product?.variants.length
                    : 1,
                },
              },
            ],
          },
          rays: [extensions?.ray || ''],
          experiments: extensions?.experiments,
          requestData: {
            ...(extensions?.LoggerLinkData || {
              start_timestamp: 0,
              duration_ms: 0,
            }),
            url: window.location.href,
          },
        }),
      })
  }

  const [updateQuantity] = useMutation<
    { updateProductQuantityInBasket: UpdateItemQuantityData },
    UpdateItemQuantityVariables
  >(UPDATE_PRODUCT_QUANTITY_MUTATION, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      data &&
        data.updateProductQuantityInBasket &&
        setBasketId(data.updateProductQuantityInBasket.id)
      quantityIsUpdating.current = false
    },
  })

  const [removeItem] = useMutation<
    { removeProductFromBasket: RemoveItemData },
    RemoveItemVariables
  >(REMOVE_PRODUCT_MUTATION, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      data &&
        data.removeProductFromBasket &&
        setBasketId(data.removeProductFromBasket.id)

      trackEnhancedEvent('removeFromCart', 'remove', props.quantity)
      trackBackendEvent(-props.quantity, 'trash')
    },
  })
  const i18nText = {
    removeItemLabel: i18n('basket.removeitem.label'),
    increaseButtonLabel: i18n('general.quantity.increase'),
    decreaseButtonLabel: i18n('general.quantity.decrease'),
    quantityLabel: i18n('general.quantity.text'),
    freeGift: i18n('basket.freegift.text'),
    free: i18n('product.freeprice.text'),
    maxOrderText: i18n(
      'basket.item.max.quantity.reached',
      maxProductLimit.toString(),
    ),
    productOutOfStockError: i18n('basket.item.product.outofstock.error.text'),
  }

  const wishlistI18nText = {
    addToWishlistText: i18n('product.addtowishlistbutton.addtowishlist.text'),
    savedToWishlistText: i18n(
      'product.addtowishlistbutton.savedtowishlist.text',
    ),
    wishlistTooltip: {
      closeButtonText: i18n('product.addtowishlist.tooltip.button.close.text'),
    },
    wishlistTooltipContent: {
      loginSignupText: i18n('product.addtowishlist.tooltip.loginsignup.text'),
      toUseWishlistText: i18n('product.addtowishlist.tooltip.text'),
    },
  }

  const quantitySelectorProps: QuantitySelectorProps = {
    i18nText: {
      quantityLabel: i18nText.quantityLabel,
      decreaseButtonLabel: i18nText.decreaseButtonLabel,
      increaseButtonLabel: i18nText.increaseButtonLabel,
    },
    disabled: removePressed.current || quantityIsUpdating.current,
    maxValue: maxProductLimit,
    quantity: props.quantity,
    quantityChangedCallback: debounce(async (quantity) => {
      if (quantity === 0) {
        removeItem({
          variables: {
            basketId,
            settings: sessionSettings,
            itemId: props.id,
          },
        })
      } else if (quantity) {
        quantityIsUpdating.current = true

        await updateQuantity({
          variables: {
            quantity,
            basketId,
            settings: sessionSettings,
            itemId: props.id,
          },
        })

        if (props.quantity < quantity) {
          trackBackendEvent(quantity, 'quantity_increase')
          trackEnhancedEvent('addToCart', 'add', quantity - props.quantity)
        } else {
          trackBackendEvent(quantity, 'quantity_decrease')
          trackEnhancedEvent(
            'removeFromCart',
            'remove',
            Math.abs(quantity - props.quantity),
          )
        }
      }
    }, 50),
    showInnerBorders: true,
  }

  const discountMessages =
    props.discountMessages?.map((message, index) => (
      <StyledPlatformMessage
        key={`${message}-${index}`}
        text={props.freeGift ? i18nText.freeGift : message}
        type="success"
      />
    )) || []

  return (
    <React.Fragment>
      <Container
        hasDiscounts={hasDiscounts}
        priceHidden={basketHideInitialPriceColumn}
        showWishlist={showWishlist}
        maxOrderVisible={maxProductQuantityReached}
        showClickAndCollect={clickAndCollectEnabled}
        showItemErrors={showItemErrors}
        outOfStockError={!props.product.inStock}
      >
        <ProductImageLink href={props.product.product?.url}>
          <ProductImage
            alt={props.product.title}
            urls={{
              largeProduct: props.product.product?.images?.[0]?.largeProduct,
            }}
            width={[73]}
          />
        </ProductImageLink>
        <Name href={props.product.product?.url}>{props.product.title}</Name>
        {discountMessages.length > 0 && (
          <Discount data-testid="discount-container">
            {discountMessages}
          </Discount>
        )}
        {showWishlist && (
          <WishlistContainer
            i18nText={wishlistI18nText}
            sku={props.product.sku}
            inWishlist={props.product.inWishlist || false}
          />
        )}
        <UnitPrice
          hide={basketHideInitialPriceColumn}
          data-testid="initial-price"
          fontStyle={theme?.pageTheme?.table?.font?.style}
        >
          {!props.freeGift ? props.standardPricePerUnit.displayValue : ''}
        </UnitPrice>
        <QuantitySelectorContainer>
          {!props.freeGift ? (
            <QuantitySelector {...quantitySelectorProps} />
          ) : (
            <QuantityPlaceholder>{props.quantity}</QuantityPlaceholder>
          )}
        </QuantitySelectorContainer>
        {maxProductQuantityReached && (
          <MaxOrderMessage
            data-testid="max-order-container"
            type="info"
            text={i18nText.maxOrderText}
            hideIconMobile={true}
            renderAnnouncer={renderAnnouncer}
          />
        )}
        <TotalPrice fontStyle={theme?.pageTheme?.table?.font?.style}>
          {!props.freeGift
            ? props.totalChargePrice.displayValue
            : i18nText.free}
        </TotalPrice>
        <RemoveItemContainer>
          {!props.freeGift && (
            <RemoveItem
              onClick={() => {
                removeItem({
                  variables: {
                    basketId,
                    settings: sessionSettings,
                    itemId: props.id,
                  },
                })
                removePressed.current = true
              }}
              aria-label={i18nText.removeItemLabel}
              data-testid="remove-product"
            >
              <IconContainer>
                {theme?.pageTheme?.table?.icon?.svgPath ? (
                  <svg
                    height={theme.pageTheme.table.icon.height || '24px'}
                    width={theme.pageTheme.table.icon.width || '24px'}
                    viewBox={theme.pageTheme.table.icon.viewBox || '0 0 24 24'}
                  >
                    <path
                      d={theme.pageTheme.table.icon.svgPath}
                      fillRule="evenodd"
                    />
                  </svg>
                ) : (
                  <StyledClose />
                )}
              </IconContainer>
            </RemoveItem>
          )}
        </RemoveItemContainer>
        {clickAndCollectEnabled && (
          <React.Fragment>
            <div
              style={{
                gridArea: 'clickandcollect',
                justifySelf: 'flex-start',
                alignSelf: 'flex-start',
              }}
              data-testid="click-and-collect-button"
            >
              <Button
                emphasis="low"
                onClick={() => presentClickAndCollectModal?.current()}
              >
                Click & Collect
              </Button>
            </div>
            <div
              style={{
                gridArea: 'itemerrors',
                justifySelf: 'flex-start',
                alignSelf: 'flex-start',
              }}
            >
              {props.messages && props.fulfilmentMethod && (
                <Message
                  messages={props.messages}
                  fulfilmentMethod={props.fulfilmentMethod}
                />
              )}
            </div>
          </React.Fragment>
        )}
        {showBasketItemOutOfStockMessage && !props.product.inStock && (
          <div
            style={{
              gridArea: 'outofstockerror',
              justifySelf: 'flex-start',
              alignSelf: 'flex-start',
            }}
            data-testid="out-of-stock-error"
          >
            <PlatformMessage
              type="error"
              text={i18nText.productOutOfStockError}
            />
          </div>
        )}
      </Container>
    </React.Fragment>
  )
}
